import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from '../../services/adminService';

const PAGE_LIMIT = 50;

const TYPE_META = {
  purchase:           { label: 'Compras',                color: '#2ca9e1', bg: '#e8f6fd' },
  artist_application: { label: 'Solicitudes de artista', color: '#e8640c', bg: '#fdf2ec' },
  artist_approved:    { label: 'Artistas aprobados',     color: '#28a745', bg: '#eaf7ed' },
  artist_rejected:    { label: 'Solicitudes rechazadas', color: '#888',    bg: '#f0f0f0' },
  low_stock:          { label: 'Stock bajo',             color: '#f0a500', bg: '#fef9e7' },
};

const TYPE_OPTIONS = [
  { value: '',                   label: 'Todos los tipos' },
  { value: 'purchase',           label: 'Compras' },
  { value: 'artist_application', label: 'Solicitudes de artista' },
  { value: 'artist_approved',    label: 'Artistas aprobados' },
  { value: 'artist_rejected',    label: 'Solicitudes rechazadas' },
  { value: 'low_stock',          label: 'Stock bajo' },
];

const formatDateTime = (iso) =>
  new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });

const TypeBadge = ({ type }) => {
  const cfg = TYPE_META[type] || { label: type, color: '#666', bg: '#f0f0f0' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '12px',
      fontSize: '0.78rem',
      fontWeight: '600',
      color: cfg.color,
      background: cfg.bg,
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
};

const inputStyle = {
  padding: '0.5rem 0.75rem',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '0.9rem',
  outline: 'none',
};

// Entity → admin route. Falls back to no link when the entity isn't routable.
const deepLinkFor = (n) => {
  if (!n.entityId) return null;
  switch (n.entityTable) {
    case 'Purchases':            return `/admin/orders/${n.entityId}`;
    case 'artist_applications':  return `/admin/artist-applications/${n.entityId}`;
    case 'artists':              return `/admin/artists/${n.entityId}`;
    case 'ttf_piece_requests':   return '/admin/ttf-pieces';
    default:                     return null;
  }
};

const AdminNotificationsPage = () => {
  const { session } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [type, setType] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);

  useEffect(() => { setPage(1); }, [type, unreadOnly]);

  const load = useCallback(() => {
    if (!session?.access_token) {
      setLoading(false);
      setError('No hay sesión activa.');
      return () => {};
    }
    let cancelled = false;
    setLoading(true);

    const params = { page, limit: PAGE_LIMIT };
    if (unreadOnly) params.unread = 'true';
    if (type) params.type = type;

    getNotifications(session.access_token, params)
      .then((data) => {
        if (cancelled) return;
        setNotifications(data.notifications || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [session?.access_token, page, type, unreadOnly]);

  useEffect(() => load(), [load]);

  const handleToggleRead = async (n) => {
    setBusyId(n.id);
    try {
      const { notification } = await markNotificationRead(
        session.access_token,
        n.id,
        !n.isRead
      );
      setNotifications((prev) => prev.map((row) => (row.id === n.id ? notification : row)));
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const handleMarkAllRead = async () => {
    if (!window.confirm('¿Marcar todas las notificaciones como leídas?')) return;
    setMarkingAll(true);
    try {
      await markAllNotificationsRead(session.access_token);
      load();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <div className="container" style={{ padding: '2rem 0 4rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <Link to="/admin" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>← Panel</Link>
        <h1 style={{ fontFamily: "'Gambado Sans', sans-serif", fontSize: '1.6rem', color: '#333', margin: 0 }}>
          Notificaciones
        </h1>
        <span style={{ color: '#888', fontSize: '0.9rem', marginLeft: 'auto' }}>
          {total} notificaci{total === 1 ? 'ón' : 'ones'}
        </span>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', margin: '1.5rem 0', alignItems: 'center' }}>
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          style={{ ...inputStyle, cursor: 'pointer' }}
        >
          {TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#555', fontSize: '0.9rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(e) => setUnreadOnly(e.target.checked)}
          />
          Sólo no leídas
        </label>
        <button
          type="button"
          onClick={handleMarkAllRead}
          disabled={markingAll}
          style={{
            marginLeft: 'auto',
            padding: '0.5rem 1rem',
            border: '1.5px solid #2ca9e1',
            borderRadius: '8px',
            background: 'white',
            color: '#2ca9e1',
            cursor: markingAll ? 'wait' : 'pointer',
            fontWeight: '600',
            fontSize: '0.85rem',
          }}
        >
          {markingAll ? 'Marcando...' : 'Marcar todas como leídas'}
        </button>
      </div>

      {/* Error */}
      {error && <p style={{ color: '#dc3545', marginBottom: '1rem' }}>{error}</p>}

      {/* List */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        {loading ? (
          <p style={{ padding: '2.5rem', textAlign: 'center', color: '#888', margin: 0 }}>Cargando...</p>
        ) : notifications.length === 0 ? (
          <p style={{ padding: '2.5rem', textAlign: 'center', color: '#888', margin: 0 }}>
            No hay notificaciones.
          </p>
        ) : (
          notifications.map((n) => {
            const link = deepLinkFor(n);
            return (
              <div
                key={n.id}
                style={{
                  display: 'flex',
                  gap: '1rem',
                  padding: '1rem 1.5rem',
                  borderBottom: '1px solid #f0f0f0',
                  background: n.isRead ? 'white' : '#f9fcff',
                  alignItems: 'flex-start',
                }}
              >
                {/* Unread dot */}
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  marginTop: '0.55rem',
                  background: n.isRead ? 'transparent' : '#2ca9e1',
                  flexShrink: 0,
                }} />
                {/* Main */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                    <TypeBadge type={n.type} />
                    <span style={{ color: '#888', fontSize: '0.8rem' }}>{formatDateTime(n.createdAt)}</span>
                  </div>
                  <p style={{ margin: '0.25rem 0', fontWeight: n.isRead ? '500' : '700', color: '#333' }}>
                    {n.title}
                  </p>
                  {n.body && (
                    <p style={{ margin: '0.25rem 0 0', color: '#666', fontSize: '0.88rem' }}>{n.body}</p>
                  )}
                </div>
                {/* Actions */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', alignItems: 'flex-end', flexShrink: 0 }}>
                  {link && (
                    <Link
                      to={link}
                      style={{ color: '#2ca9e1', textDecoration: 'none', fontWeight: '500', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
                    >
                      Ver →
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={() => handleToggleRead(n)}
                    disabled={busyId === n.id}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#888',
                      fontSize: '0.8rem',
                      cursor: busyId === n.id ? 'wait' : 'pointer',
                      padding: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {busyId === n.id
                      ? '...'
                      : n.isRead
                        ? 'Marcar no leída'
                        : 'Marcar leída'}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1.5rem', justifyContent: 'center' }}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #ddd',
              borderRadius: '8px',
              background: page === 1 ? '#f8f9fa' : 'white',
              color: page === 1 ? '#aaa' : '#333',
              cursor: page === 1 ? 'default' : 'pointer',
              fontSize: '0.9rem',
            }}
          >
            ← Anterior
          </button>
          <span style={{ color: '#666', fontSize: '0.9rem' }}>
            Página {page} de {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #ddd',
              borderRadius: '8px',
              background: page === totalPages ? '#f8f9fa' : 'white',
              color: page === totalPages ? '#aaa' : '#333',
              cursor: page === totalPages ? 'default' : 'pointer',
              fontSize: '0.9rem',
            }}
          >
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminNotificationsPage;
